import { BestMatchType, PackType } from "@is/constants";

import { MediaType } from "./media";

export type Currency = {
  countryCode: string;
  currencyCode: string;
};

export type Material = {
  name: string;
  percentage: string;
};

export type Price = Currency & {
  retailPrice: number;
  wholesalePrice: number;
  markup: number;
};

export type Color = {
  name: string;
  mainColorName: string;
};

export type Size = {
  size: string;
  ean: string;
  length?: string;
  instoreQuantity?: number;
  onlineQuantity?: number;
  prices?: Price[];
};

export type ColorVariant = {
  id: string;
  name?: string;
  color: Color;
  media: MediaType[];
  sizeInfos?: Size[];
  prices?: Price[];
};

export type Attribute = {
  key: string;
  value: string;
  label: string;
};

export type Category = {
  subCategories: any;
  name: string;
};

export type Style = {
  id: string;
  ean?: string;
  brand?: string;
  gender?: string;
  categories?: Category[];
  name?: string;
  prices?: Price[];
  media?: MediaType[];
  description?: string;
  variants?: ColorVariant[];
  attributes?: Attribute[];
  materialComposition?: Material[];
  createdAt?: string;
  updatedAt?: string;
};

export type SuggestedStyleType = {
  id: string;
  name: string;
  price: {
    currencyCode: string;
    retailPrice: number;
  };
  media: MediaType[];
};

export type ProductPrice = {
  currencyCode: string;
  retailPrice: number;
  countryCode?: string;
  salePrice?: number;
};

export type ProductPriceWithRange = {
  currencyCode: string;
  retailPrice: string;
  countryCode?: string;
  salePrice?: string;
};

export type Meta = {
  attributeScore: number;
  clipScore: number;
  descriptionScore: number;
  matchedAllVariantAttributes: boolean;
  nameLikeScore: number;
  nameTrigramScore: number;
  segmentAttributeScore: number;
  type: BestMatchType;
};

type ProductColorVariant = {
  styleId: string;
  id: string;
  name: string;
  color: string;
  media: MediaType[];
  sizeInfos?: Size[];
  price: ProductPrice;
  meta?: Meta;
};

export type ProductItem = ProductColorVariant & {
  // when we need to preselect the size
  preselectedSize?: Size;
  otherVariants?: ProductColorVariant[];
  searchSizesAndLengths?: SearchSizesAndLengths;
  styleDetails?: Style;
  widgetProductItemId?: string;
  isPDPOpen?: boolean;
  isWidgetsOpen?: boolean;
  packType?: PackType;
};

export type PreferenceOption = {
  label?: string;
  key: string;
  parentKey?: string;
  options?: PreferenceOption[];
  parentIndex?: number;
  type?: SearchTermType;
  custom?: boolean;
  categoryKey?: string;
};

export enum SearchTermType {
  Attribute = "attribute",
  Category = "category",
  Subcategory = "subcategory",
  Color = "color",
  Gender = "gender",
  Size = "size",
  Length = "length",
  Materials = "materials",
  Text = "text",
  Scanned = "scanned",
  Price = "price",
}

export const SearchTermTypeLabels = {
  [SearchTermType.Attribute]: "Attribute",
  [SearchTermType.Category]: "Category",
  [SearchTermType.Subcategory]: "Subcategory",
  [SearchTermType.Color]: "Color",
  [SearchTermType.Gender]: "Gender",
  [SearchTermType.Size]: "Size",
  [SearchTermType.Length]: "Length",
  [SearchTermType.Price]: "Price",
};

export enum AttributeType {
  Sizes = "sizes",
  Length = "length",
  Category = "category",
  Price = "price",
}

export type SearchTerm = {
  type: SearchTermType;
  key?: string;
  value: string;
  label?: string;
  isPrimary?: boolean;
  categoryKey?: string;
  min?: number;
  max?: number;
  currency?: string;
  country?: string;
};

export type SearchSizesAndLengths = {
  sizes: string[];
  lengths: string[];
};

export type PriceRange = {
  min: number | null;
  max: number | null;
};
